import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { Booking } from '../booking';
import ViewMergeBooking from './ViewMergeBooking';

interface MergeSuggestion {
  groupId: string;
  bookings: Booking[] | null | undefined;
  vehicleSuggestions: string[];
  driverSuggestions: string[];
  suggestedDepartureTime: string | null;
  suggestedArrivalTime: string | null;
  comment: string | null;
}

interface MergeRequestModalProps {
  show: boolean;
  onHide: () => void;
  mergeDetails: MergeSuggestion[];
  mergeModalError: string | null;
  handleViewClick: (booking: Booking) => void;
  handleAcceptMerge: (groupId: string, numberPlate: string | null, empNumber: string | null) => Promise<boolean>;
  handleRejectMerge: (groupId: string, comment: string) => Promise<boolean>;
  statusColors: { [key: string]: string };
  isMerging: boolean;
}

const MergeRequestModal: React.FC<MergeRequestModalProps> = ({
  show,
  onHide,
  mergeDetails,
  mergeModalError,
  handleViewClick,
  handleAcceptMerge,
  handleRejectMerge,
  statusColors,
  isMerging,
}) => {
  const [selectedGroup, setSelectedGroup] = useState<MergeSuggestion | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Validate mergeDetails on mount or change
  useEffect(() => {
    if (show) {
      setIsLoading(true);
      setError(null);
      try {
        if (!Array.isArray(mergeDetails)) {
          setError('Invalid merge suggestions data.');
          return;
        }
        const validGroups = mergeDetails.filter((group, index) => {
          if (!group?.groupId || group.bookings === null || group.bookings === undefined || !Array.isArray(group.bookings)) {
            return false;
          }
          return true;
        });
        if (validGroups.length === 0 && mergeDetails.length > 0) {
          setError('No valid merge groups found.');
        }
      } catch (err) {
        setError('Failed to process merge suggestions.');
      } finally {
        setIsLoading(false);
      }
    }
  }, [show, mergeDetails]);

  // Logging for debugging
  useEffect(() => {

  }, [show, mergeDetails, mergeModalError, isMerging, selectedGroup]);

  const splitTrips = (bookings: Booking[] | null | undefined): { tripOne: Booking[]; tripTwo: Booking[] } => {
    if (!Array.isArray(bookings) || bookings.length === 0) {
      return { tripOne: [], tripTwo: [] };
    }
    const sortedBookings = [...bookings].sort((a, b) => Number(a.departureTime) - Number(b.departureTime));
    if (sortedBookings.length === 0) {
      return { tripOne: [], tripTwo: [] };
    }
    const earliestDeparture = sortedBookings[0].departureTime;
    const tripOne = sortedBookings.filter((booking) => booking.departureTime === earliestDeparture);
    const tripTwo = sortedBookings.filter((booking) => booking.departureTime !== earliestDeparture);
    return { tripOne, tripTwo };
  };

  const getTripLocations = (bookings: Booking[] | null | undefined): string[] => {
    if (!Array.isArray(bookings)) {
      return ['N/A'];
    }
    const allLocations = bookings.flatMap((booking) =>
      Array.isArray(booking.locations)
        ? booking.locations.map((loc) => loc.address || 'N/A')
        : ['N/A']
    );
    return allLocations.length > 0 ? allLocations : ['N/A'];
  };

  const getTotalEstimatedKms = (bookings: Booking[] | null | undefined): number => {
    if (!Array.isArray(bookings)) {
      return 0;
    }
    const total = bookings.reduce((sum, booking) => sum + (Number(booking.estimatedKms) || 0), 0);
    return total;
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        autoFocus={false}
        enforceFocus={false}
        aria-labelledby="merge-suggestions"
        inert={selectedGroup ? 'inert' : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title id="merge-suggestions">Merge Suggestions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading && <Alert variant="info">Loading merge suggestions...</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          {mergeModalError && <Alert variant="danger">{mergeModalError}</Alert>}
          {!isLoading && !error && !mergeModalError && (!mergeDetails || mergeDetails.length === 0) && (
            <Alert variant="info">No merge suggestions available.</Alert>
          )}
          {!isLoading && !error && !mergeModalError && mergeDetails?.length > 0 && (
            <div className="table-responsive">
              <table className="table table-striped text-wrap">
                <thead className="table-primary">
                  <tr>
                    <th>Group ID</th>
                    <th>Trip One Locations</th>
                    <th>Trip Two Locations</th>
                    <th>Estimated KM</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mergeDetails.map((group, index) => {
                    if (!group?.groupId || group.bookings === null || group.bookings === undefined || !Array.isArray(group.bookings)) {
                      return null;
                    }
                    const { tripOne, tripTwo } = splitTrips(group.bookings);
                    return (
                      <tr key={group.groupId}>
                        <td>{group.groupId}</td>
                        <td>
                          {getTripLocations(tripOne).map((address, index) => (
                            <div key={`${group.groupId}-trip1-${index}`}>{address}</div>
                          ))}
                        </td>
                        <td>
                          {getTripLocations(tripTwo).map((address, index) => (
                            <div key={`${group.groupId}-trip2-${index}`}>{address}</div>
                          ))}
                        </td>
                        <td>{getTotalEstimatedKms(group.bookings).toFixed(2)} km</td>
                        <td>
                          <Button
                            variant="link"
                            className="btn btn-icon btn-sm btn-soft-info rounded-pill"
                            onClick={() => {
                              setSelectedGroup(group);
                            }}
                            title="View Merge Group"
                            disabled={isMerging || isLoading}
                            aria-label={`View merge group ${group.groupId}`}
                          >
                            <i className="feather-info" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={isMerging || isLoading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {selectedGroup && Array.isArray(selectedGroup.bookings) && (
        <ViewMergeBooking
          show={!!selectedGroup}
          onHide={() => {
            setSelectedGroup(null);
          }}
          mergeGroup={selectedGroup}
          statusColors={statusColors}
          handleAcceptMerge={handleAcceptMerge}
          handleRejectMerge={handleRejectMerge}
          isMerging={isMerging}
        />
      )}
    </>
  );
};

export default MergeRequestModal;